/**
 * Settings information architecture (DECISIONS §19, UX-048, UX-081). Five groups
 * (simplified 2026-10-22 from seven: the one-page Team and Money groups moved
 * into Business; nothing was removed and every route is unchanged):
 *
 *   Business            — Business profile, Brand kit, Services & pricing, Tax & payments, Team
 *   Phone & texting     — one phone page with tabs (line, calls, texting, quiet hours), usage
 *   Booking & calendar  — online booking, working hours, calendar feed
 *   Integrations        — connected apps (Google, email, website form, Lead Finder), API keys
 *   You                 — profile, notifications, password
 *
 * THE single source of Settings routes and labels. Every section is a route,
 * /settings/<id>; the phone page adds /settings/phone/<tab>. Links anywhere in
 * the app use settingsPath(key) / settingsLabel(key) from here
 * (lib/settingsRoutes re-exports them). Old routes keep working through
 * LEGACY_SETTINGS_REDIRECTS.
 */

import type { PhaseTone } from '@/lib/phaseTone'
import type React from 'react'
import {
  Bell, Briefcase, Building2, Calendar as CalendarIcon, CalendarClock, CreditCard,
  Gauge, KeyRound, Link as LinkIcon, Lock, Palette, Phone, Tags, User, Users,
} from 'lucide-react'
import type { CapabilityName } from '@/lib/permissions'
import { SETTINGS_SECTION_CAPABILITY } from '@/components/Navigation/navItems'

export type SettingsGroupId = 'business' | 'phone' | 'calendar' | 'integrations' | 'you'

export const SETTINGS_GROUPS: { id: SettingsGroupId; label: string }[] = [
  { id: 'business',     label: 'Business' },
  { id: 'phone',        label: 'Phone & texting' },
  { id: 'calendar',     label: 'Booking & calendar' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'you',          label: 'You' },
]

export type SettingsSectionId =
  | 'business' | 'brand' | 'pricing'
  | 'team'
  | 'phone' | 'usage'
  | 'money'
  | 'booking' | 'availability' | 'calendar'
  | 'integrations' | 'api'
  | 'profile' | 'notifications' | 'security'

export interface SettingsSectionDef {
  id: SettingsSectionId
  group: SettingsGroupId
  label: string
  subtitle: string
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  /** Icon tile colour, iOS Settings style: wayfinding, not a phase of work. */
  tone: PhaseTone | 'rose' | 'neutral'
  /** Hidden from people without this capability (server enforces it too). */
  capability?: CapabilityName
  /** Extra words people search for. */
  keywords?: string
}

// Icon tiles are coloured the iOS Settings way (DECISIONS §26): the colour
// helps you find a row again; it isn't a phase of work (lib/phaseTone).
const SECTION_LIST: Omit<SettingsSectionDef, 'capability'>[] = [
  { id: 'business', group: 'business', label: 'Business profile', subtitle: 'Name, contact details, license, time zone', icon: Building2, tone: 'sky', keywords: 'company legal name email website license address timezone time zone google review link' },
  { id: 'brand', group: 'business', label: 'Brand kit', subtitle: 'Logo and colors on quotes and invoices', icon: Palette, tone: 'rose', keywords: 'logo color colour pdf quote invoice look test' },
  { id: 'pricing', group: 'business', label: 'Services & pricing', subtitle: 'Service types and price book', icon: Tags, tone: 'peach', keywords: 'price book catalog items service types trades' },
  { id: 'money', group: 'business', label: 'Tax & payments', subtitle: 'Sales tax, card fees, Stripe, QuickBooks', icon: CreditCard, tone: 'mint', keywords: 'money sales tax rate taxable card fee surcharge stripe quickbooks how to pay payment terms' },
  // Mount point the Team & roles lane fills.
  { id: 'team', group: 'business', label: 'Team', subtitle: 'People, roles and invites', icon: Users, tone: 'lavender', keywords: 'users invite roles permissions crew office staff' },

  { id: 'phone', group: 'phone', label: 'Phone & texting', subtitle: 'Your line, who rings, texting, quiet hours', icon: Phone, tone: 'mint', keywords: 'twilio numbers ring groups routing hours voicemail missed call ai receptionist text sms a2p 10dlc registration brand campaign quiet hours' },
  { id: 'usage', group: 'phone', label: 'Usage', subtitle: 'Calls, texts and AI this month', icon: Gauge, tone: 'butter', keywords: 'billing usage minutes sms cost cap' },

  { id: 'booking', group: 'calendar', label: 'Online booking', subtitle: 'Booking link, appointments, reminders', icon: Briefcase, tone: 'sky', keywords: 'booking page link services appointments reminders confirmation email cancel' },
  { id: 'availability', group: 'calendar', label: 'Working hours', subtitle: 'When you can be booked, calendar privacy', icon: CalendarClock, tone: 'blush', keywords: 'availability your hours schedule privacy busy' },
  { id: 'calendar', group: 'calendar', label: 'Calendar feed', subtitle: 'See bookings in Apple, Google or Outlook', icon: CalendarIcon, tone: 'blush', keywords: 'ical ics subscribe sync teammates calendar links' },

  { id: 'integrations', group: 'integrations', label: 'Connected apps', subtitle: 'Google reviews, Calendar, Ads, send mail, SendGrid, website form', icon: LinkIcon, tone: 'lavender', keywords: 'integrations connected google reviews reputation gbp business profile ads google ads meta ads spend gmail calendar send mail sendgrid email website lead form lead finder sources workspace id' },
  { id: 'api', group: 'integrations', label: 'API keys', subtitle: 'Keys for AI assistants and other software', icon: KeyRound, tone: 'neutral', keywords: 'integrations api key mcp agent token' },

  { id: 'profile', group: 'you', label: 'Profile', subtitle: 'Your name, mobile number and appearance', icon: User, tone: 'neutral', keywords: 'name phone mobile me appearance theme light dark mode ios aurora colours colors username sign-in email' },
  { id: 'notifications', group: 'you', label: 'Notifications', subtitle: 'What you hear about, and how', icon: Bell, tone: 'blush', keywords: 'alerts email text push app' },
  { id: 'security', group: 'you', label: 'Password & sign-in', subtitle: 'Username, email, password, other devices', icon: Lock, tone: 'neutral', keywords: 'password security sign in sign out devices email username login change email change username' },
]

/**
 * Gating comes from one table shared with the nav and the contract test
 * (SETTINGS_SECTION_CAPABILITY, F1 dotted keys) so it can't drift.
 */
export const SETTINGS_SECTIONS: SettingsSectionDef[] = SECTION_LIST.map((s) => ({
  ...s,
  capability: SETTINGS_SECTION_CAPABILITY[s.id],
}))

const SECTION_IDS = new Set<string>(SETTINGS_SECTIONS.map((s) => s.id))

// ─── Phone tabs (/settings/phone/<tab>) ───────────────────────────────────────

/** Tabs inside the phone page; components/Settings/phone/PhoneSettings renders them. */
export const PHONE_TABS = [
  { id: 'line', label: 'Line & numbers' },
  { id: 'calls', label: 'Calls & ring groups' },
  { id: 'texting', label: 'Texting & registration' },
  { id: 'quiet-hours', label: 'Quiet hours' },
] as const

export type PhoneTabId = (typeof PHONE_TABS)[number]['id']

// ─── Link map ─────────────────────────────────────────────────────────────────

const sectionLabel = (id: SettingsSectionId) => SETTINGS_SECTIONS.find((s) => s.id === id)!.label
const phoneTabLabel = (id: PhoneTabId) => PHONE_TABS.find((t) => t.id === id)!.label

/**
 * Every in-app link into Settings (UX-088). `path` is the route; `label` is how
 * copy names the place ("Settings → Phone & texting").
 */
export const SETTINGS_ROUTES = {
  ...(Object.fromEntries(
    SECTION_LIST.map((s) => [s.id, { path: `/settings/${s.id}`, label: `Settings → ${s.label}` }]),
  ) as Record<SettingsSectionId, { path: string; label: string }>),
  phoneLine: { path: '/settings/phone/line', label: `Settings → Phone & texting → ${phoneTabLabel('line')}` },
  phoneCalls: { path: '/settings/phone/calls', label: `Settings → Phone & texting → ${phoneTabLabel('calls')}` },
  texting: { path: '/settings/phone/texting', label: `Settings → Phone & texting → ${phoneTabLabel('texting')}` },
  quietHours: { path: '/settings/phone/quiet-hours', label: `Settings → Phone & texting → ${phoneTabLabel('quiet-hours')}` },
  /** Billing tools live outside Settings; they're opened from Tax & payments. */
  billingTools: { path: '/payments/tools', label: `Billing tools (Settings → ${sectionLabel('money')})` },
} as const

export type SettingsRouteKey = keyof typeof SETTINGS_ROUTES

/** The route for a section or settings page. */
export function settingsPath(key: SettingsRouteKey): string {
  return SETTINGS_ROUTES[key].path
}

/** How copy names a settings page: "Settings → Usage". */
export function settingsLabel(key: SettingsRouteKey): string {
  return SETTINGS_ROUTES[key].label
}

// ─── Old routes ───────────────────────────────────────────────────────────────

/** Old /settings/<segment> routes (bookmarks, emails, notices) → their new home. */
export const LEGACY_SETTINGS_REDIRECTS: Record<string, string> = {
  services: settingsPath('booking'),
  twilio: settingsPath('phoneLine'),
  a2p: settingsPath('texting'),
  texting: settingsPath('texting'),
  telephony: settingsPath('phone'),
  billing: settingsPath('usage'),
  workspace: settingsPath('business'),
  tax: settingsPath('money'),
  payments: settingsPath('money'),
  stripe: settingsPath('money'),
  quickbooks: settingsPath('money'),
}

/** A URL segment → the section to show, or null when it isn't a current section. */
export function resolveSectionId(segment: string): SettingsSectionId | null {
  return SECTION_IDS.has(segment) ? (segment as SettingsSectionId) : null
}

/** Where an old /settings/<segment> route now lives, or null. */
export function legacySettingsRedirect(segment: string): string | null {
  return LEGACY_SETTINGS_REDIRECTS[segment] ?? null
}

/**
 * Does `to` (a path, optionally with ?query/#hash) open a real Settings page?
 * Old routes count only through their redirect target. Used by tests to keep
 * every link in the app honest.
 */
export function isSettingsRoute(to: string): boolean {
  const path = to.split(/[?#]/)[0].replace(/\/+$/, '')
  if (path === '/settings') return true
  if (path === settingsPath('billingTools')) return true
  const m = /^\/settings\/([^/]+)(?:\/([^/]+))?$/.exec(path)
  if (!m || !resolveSectionId(m[1])) return false
  if (!m[2]) return true
  return m[1] === 'phone' && PHONE_TABS.some((t) => t.id === m[2])
}

/** First section a person can open (desktop opens it at /settings). */
export function defaultSectionFor(visible: SettingsSectionDef[]): SettingsSectionId {
  return visible[0]?.id ?? 'profile'
}

export function searchSections(sections: SettingsSectionDef[], query: string): SettingsSectionDef[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return sections
  return sections.filter((s) => {
    const group = SETTINGS_GROUPS.find((g) => g.id === s.group)?.label ?? ''
    const hay = `${s.label} ${s.subtitle} ${group} ${s.keywords ?? ''}`.toLowerCase()
    return words.every((w) => hay.includes(w))
  })
}
